import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BenefitsBrandPanel from "../../components/BenefitsBrandPanel";
import { makePost } from "../../utils/fetchUtils";
import {
  SEND_EMAIL_VERIFICATION_LINK,
  VERIFY_EMAIL_TOKEN,
} from "../../utils/constants";
import { saveBenefitsSession } from "../../utils/benefitsSession";
import "../InfoAccess/BenefitsExperience.css";

type VerificationState = "verifying" | "success" | "error";

const VerifyEmailToken = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("token") || "";
  const headingRef = useRef<HTMLHeadingElement>(null);
  const verificationStartedRef = useRef("");
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [verificationState, setVerificationState] = useState<VerificationState>(
    linkToken ? "verifying" : "error"
  );
  const [message, setMessage] = useState(
    linkToken ? "Verifying your email…" : "This verification link is incomplete."
  );
  const [resendEmail, setResendEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState("");

  useEffect(() => {
    document.title = "Verify your email | NClude Benefits";
    headingRef.current?.focus();
  }, [verificationState]);

  useEffect(() => {
    if (!linkToken || verificationStartedRef.current === linkToken) return;
    verificationStartedRef.current = linkToken;

    const verifyEmailLink = async () => {
      try {
        const response = await makePost(
          VERIFY_EMAIL_TOKEN,
          JSON.stringify({ token: linkToken, purpose: "benefits" })
        );

        if (response?.success === false) {
          throw new Error(response.message || "This verification link has expired or is invalid.");
        }

        const accessToken = response?.accessToken || response?.token;
        const refreshToken = response?.refreshToken;
        if (!accessToken || !refreshToken) {
          throw new Error("Your email was verified, but a Benefits session could not be started.");
        }

        saveBenefitsSession({
          token: accessToken,
          refreshToken,
          expiresInSeconds: response?.expiresInSeconds,
          refreshExpiresInSeconds: response?.refreshExpiresInSeconds,
        });
        setVerificationState("success");
        setMessage("Email verified. Opening your Benefits Assistant…");
        redirectTimeoutRef.current = setTimeout(() => {
          navigate("/chat", { replace: true });
        }, 900);
      } catch (verificationError: any) {
        setVerificationState("error");
        setMessage(
          verificationError?.response?.data?.message ||
            verificationError?.message ||
            "This verification link has expired or is invalid."
        );
      }
    };

    verifyEmailLink();

    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    };
  }, [linkToken, navigate]);

  const resendVerificationLink = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedEmail = resendEmail.trim().toLowerCase();
    if (!normalizedEmail || isResending) return;

    setIsResending(true);
    setResendStatus("");
    try {
      const response = await makePost(
        SEND_EMAIL_VERIFICATION_LINK,
        JSON.stringify({ email: normalizedEmail, purpose: "benefits" })
      );
      if (response?.success === false) {
        throw new Error(response.message || "We couldn’t send a new link.");
      }
      setResendStatus(`A new verification link was sent to ${normalizedEmail}.`);
    } catch (resendError: any) {
      setResendStatus(
        resendError?.response?.data?.message ||
          resendError?.message ||
          "We couldn’t send a new link. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  const heading = verificationState === "verifying"
    ? "Verifying your email"
    : verificationState === "success"
      ? "You’re verified"
      : "We couldn’t verify this link";

  return (
    <main className="benefits-access-page" id="main-content">
      <BenefitsBrandPanel />
      <section className="benefits-access-panel" aria-labelledby="benefits-access-heading">
        <div className="benefits-access-form-wrap benefits-verification-result">
          <p
            className={`benefits-verification-icon is-${verificationState}`}
            aria-hidden="true"
          >
            {verificationState === "success" ? "✓" : verificationState === "error" ? "!" : "…"}
          </p>
          <h2 id="benefits-access-heading" ref={headingRef} tabIndex={-1}>
            {heading}
          </h2>
          <p
            className="benefits-verification-message"
            role={verificationState === "error" ? "alert" : "status"}
            aria-live="polite"
          >
            {message}
          </p>

          {verificationState === "error" && (
            <form className="benefits-email-form benefits-resend-form" onSubmit={resendVerificationLink}>
              <label htmlFor="benefits-resend-email">Email address</label>
              <input
                id="benefits-resend-email"
                type="email"
                value={resendEmail}
                onChange={(event) => setResendEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
              <button
                type="submit"
                className="benefits-primary-button"
                disabled={isResending}
                aria-busy={isResending}
              >
                {isResending ? "Sending link…" : "Send a new verification link"}
              </button>
              {resendStatus && (
                <p className="benefits-access-announcer" role="status" aria-live="polite">
                  {resendStatus}
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default VerifyEmailToken;
